/**
 * Note: The trainer search page, as with the standard search page, uses a different breakpoint between tablet and desktop,
 * MQ.TABLET_DOWN, in order to avoid the 3-column layout on tablet devices as it becomes too cramped.
 * Please be mindful of this when adjusting the layout.
 *
 * See: https://roverdotcom.atlassian.net/browse/DEV-76306
 */

import { css, styled } from 'styled-components';

import { LAYOUT, SEARCH_PAGE_Z_INDEX } from '@rover/constants/src/search/searchPage.constants';
import Button from '@rover/kibble/core/Button';
import MQ from '@rover/kibble/styles/MediaQuery';
import { ResponsiveLayout } from '@rover/react-lib/src/components/layouts';

export const TrainerSearchPageLayout = styled(ResponsiveLayout)`
  background-color: ${({ theme }) => theme.background.secondary};
  position: relative;
  padding-bottom: 0px;
  border-bottom: ${({ theme }) =>
    `${theme.borderWidth.secondary} solid ${theme.colors.border.primary}`};
  min-height: 100vh;

  ${MQ.SM_UP.toString()} {
    padding: 5px 5px 24px 5px;
    border-bottom: 0px;
  }
`;

interface FilterSectionLayoutProps {
  stickyTopMobile?: string;
}

export const FilterSectionLayout = styled.div<FilterSectionLayoutProps>`
  display: flex;
  position: sticky;
  background-color: ${({ theme }) => theme.background.primary};
  width: ${LAYOUT.LEFT_WIDTH}px;
  border-radius: ${({ theme }) => `${theme.radii.secondary} ${theme.radii.secondary} 0px 0px`};
  margin-right: 8px;
  flex-shrink: 0;
  flex-direction: column;
  padding: 10px;
  z-index: 10;

  ${MQ.XS_DOWN.toString()} {
    width: 100%;
    border-radius: unset;
    ${({ stickyTopMobile }) => stickyTopMobile && `top: ${stickyTopMobile};`}
    margin-right: 0;
    padding: 16px;
    background: ${({ theme }) => theme.background.primary};
    border-bottom: ${({ theme }) =>
      `${theme.borderWidth.secondary} solid ${theme.colors.border.primary}`};
  }
`;

type SearchResultSectionProps = {
  isMapExpanded: boolean;
};
export const SearchResultSectionLayout = styled.div<SearchResultSectionProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-self: stretch;
  z-index: ${SEARCH_PAGE_Z_INDEX.SEARCH_RESULTS_WRAPPER};
  ${({ isMapExpanded }) => {
    const DESKTOP_WIDTH_PERCENT = isMapExpanded
      ? LAYOUT.NARROW_WIDTH_PERCENT
      : LAYOUT.WIDE_WIDTH_PERCENT;

    return css`
      flex-basis: ${DESKTOP_WIDTH_PERCENT * 100}%;

      ${MQ.TABLET_DOWN.toString()} {
        flex-basis: ${LAYOUT.WIDE_WIDTH_PERCENT * 100}%;
        background: ${({ theme }) => theme.background.secondary};
      }
    `;
  }}
`;

export const DesktopFaqSection = styled('div')`
  display: block;
  flex-basis: ${LAYOUT.NARROW_WIDTH_PERCENT * 100}%;

  ${MQ.TABLET_DOWN.toString()} {
    display: none;
  }
`;

/**
 * We conditionally display a tablet-down FAQ section outside of the
 * TrainerSearchPageLayout to force the FAQ section to wrap under the
 * FilterSectionLayout and SearchResultSectionLayout. This cannot be easily
 * achieved with a single element and a conditional flex wrap/direction or
 * grid layout due to this page being intentionally coupled to the core
 * search page's LAYOUT constant values, for consistency.
 */
export const FaqSection = styled(DesktopFaqSection)`
  display: none;
  background-color: ${({ theme }) => theme.background.secondary};

  ${MQ.TABLET_DOWN.toString()} {
    display: block;
  }
`;

interface TabletAndDownButtonProps extends React.ComponentProps<typeof Button> {
  shouldHide?: boolean;
}

// eslint-disable-next-line rover/prefer-kibble-components
export const TabletAndDownButton = styled(Button)<TabletAndDownButtonProps>`
  display: none;

  ${MQ.TABLET_DOWN.toString()} {
    display: ${({ shouldHide }) => (shouldHide ? 'none' : 'initial')};
  }
`;
